// Theme selection: light by default, darkest-blue glass as an explicit
// choice (operator decision 2026-08-24, superseding the 2026-08-19
// dark-default — the mission is a tool an average person reads at a
// glance; dark remains the brand and is one toggle away).
//
// Mirrors apps/desktop/src/theme.ts — same policy, same storage key
// semantics, kept separate because the two surfaces share design VALUES
// (tokens.css, drift-tested) but not runtime code: this file must run as
// a plain ES module with no build step.
//
// There used to be a third state, "auto", meaning "let the OS decide".
// It went with the `@media (prefers-color-scheme: light)` block in
// tokens.css: with no media query, "auto" and the default select the
// same pixels and the control has a position that does nothing. Two of
// three states being indistinguishable is worse than two honest ones.
//
// Light is the ABSENCE of data-theme rather than data-theme="light",
// because light is what bare `:root` in tokens.css now says. Only
// "dark" is ever stored — which also made the flip migration-free: the
// old scheme only ever stored "light", so a stored "light" still reads
// as light and nobody's explicit choice moved.

const KEY = "helix-theme";

export function loadTheme() {
  return localStorage.getItem(KEY) === "dark" ? "dark" : "light";
}

export function applyTheme(choice) {
  if (choice === "dark") {
    document.documentElement.dataset.theme = "dark";
    localStorage.setItem(KEY, "dark");
  } else {
    delete document.documentElement.dataset.theme;
    localStorage.removeItem(KEY);
  }
}

export function initTheme() {
  applyTheme(loadTheme());
}

/// light ⇄ dark. One button, no menu.
export function cycleTheme() {
  const next = loadTheme() === "dark" ? "light" : "dark";
  applyTheme(next);
  return next;
}

/// The two states, as icons. Drawn rather than lettered because the
/// control sits in a crowded topbar and "Theme: dark" is four times the
/// width of the thing it does.
///
/// An icon-only control has no accessible name of its own, so the label
/// still exists - it moves to aria-label and title, and it is UPDATED on
/// every change. An icon saying "dark" beside a name saying "light" is
/// worse than either alone.
///
/// The glyph shows the state you are IN, not the one you would move to.
const ICONS = {
  // Sun.
  light:
    '<circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4"/>',
  // Crescent.
  dark: '<path d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4Z"/>',
};

const THEME_LABEL = {
  light: "Theme: light",
  dark: "Theme: dark",
};

function paint(button, choice) {
  button.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
    'focusable="false">' + ICONS[choice] + "</svg>";
  const label = THEME_LABEL[choice];
  button.setAttribute("aria-label", label);
  button.setAttribute("title", label + " (click to change)");
}

/// Wire a toggle button: icon plus an honest accessible name, kept current.
export function bindThemeButton(button) {
  button.classList.add("theme-btn");
  paint(button, loadTheme());
  button.addEventListener("click", () => paint(button, cycleTheme()));
}
